import { useMemo } from 'react';

import type {
  V1CodeManifest,
  V1RunState,
} from '@/generated/hooks/jeeves/models';
import type { IFunctionCallEvent } from '@/modules/automation-run/hooks/useRunJournalEvents';

import {
  deriveStepRunStatuses,
  type TStepRunStatus,
} from '../utils/step-run-status';

/** Stable empty map, so a manifest-less render keeps one reference. */
const NO_STATUSES: ReadonlyMap<number, TStepRunStatus> = new Map();

/** Params for {@link useManifestStepStatuses}. */
interface IUseManifestStepStatusesParams {
  /** The manifest whose steps the statuses are keyed to. */
  manifest: V1CodeManifest | undefined;
  /** Finished function-call steps from the run's bishop journal. */
  events: IFunctionCallEvent[];
  /** The run's current state; drives terminal/failed/awaiting-guidance. */
  runState: V1RunState | undefined;
  /**
   * True when the journal produced no events for this run at all — a
   * terminal-success run must not be painted green off the run object alone.
   */
  eventsUnavailable?: boolean;
}

/**
 * Per-step run status keyed on the manifest's 1-based step number.
 *
 * A memo around {@link deriveStepRunStatuses}, shared so no two surfaces
 * disagree about which step is running. Steps with no `entry_function.name`
 * are dropped, since that name is what matches an event to a step.
 */
export function useManifestStepStatuses({
  manifest,
  events,
  runState,
  eventsUnavailable = false,
}: IUseManifestStepStatusesParams): ReadonlyMap<number, TStepRunStatus> {
  const steps = useMemo(
    () => (manifest?.steps ?? []).filter(step => step.entry_function?.name),
    [manifest],
  );

  return useMemo(() => {
    if (!manifest) return NO_STATUSES;
    return deriveStepRunStatuses({
      steps,
      events,
      isRunTerminal: !!(
        runState?.completed ||
        runState?.failed ||
        runState?.stopped
      ),
      isRunFailed: !!runState?.failed,
      isRunAwaitingGuidance: !!runState?.awaiting_guidance,
      failedLine: runState?.failed?.location?.start_position?.line,
      awaitingGuidanceLine:
        runState?.awaiting_guidance?.location?.start_position?.line,
      eventsUnavailable,
    });
  }, [manifest, steps, events, runState, eventsUnavailable]);
}
